import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  AlertTriangle, Clock, DollarSign, Bell, CheckCircle, 
  AlertCircle, XCircle, Send 
} from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config';
const API = BACKEND_URL;

const SmartAlerts = ({ projectId, user }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchAlerts();
    // Refresh alerts every 5 minutes
    const interval = setInterval(fetchAlerts, 300000);
    return () => clearInterval(interval);
  }, [projectId]);

  const fetchAlerts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/projects/${projectId}/alerts/pending`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Defensive: backend should always return an array but a proxy/CDN error
      // page can sneak through. Coerce to [] so child .filter()/.map() can't
      // crash the whole tab.
      setAlerts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendReminders = async () => {
    try {
      setSending(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/projects/${projectId}/alerts/send-reminders`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reminder emails sent successfully!');
    } catch (error) {
      console.error('Failed to send reminders:', error);
      toast.error('Failed to send reminders');
    } finally {
      setSending(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <XCircle className="h-5 w-5 text-red-600" />;
      case 'high': return <AlertTriangle className="h-5 w-5 text-orange-700" />;
      case 'medium': return <AlertCircle className="h-5 w-5 text-yellow-700" />;
      default: return <Bell className="h-5 w-5 text-blue-600" />;
    }
  };

  const getAlertIcon = (type) => {
    if (type.includes('task') || type.includes('milestone')) {
      return <Clock className="h-5 w-5" />;
    }
    if (type.includes('budget')) {
      return <DollarSign className="h-5 w-5" />;
    }
    if (type.includes('risk')) {
      return <AlertTriangle className="h-5 w-5" />;
    }
    return <Bell className="h-5 w-5" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const criticalAlerts = alerts.filter(a => a.severity === 'critical');
  const highAlerts = alerts.filter(a => a.severity === 'high');
  const mediumAlerts = alerts.filter(a => a.severity === 'medium');

  return (
    <div className="space-y-6">
      {/* Alert Summary */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Smart Alerts</h2>
          <p className="text-gray-600 mt-1">
            {alerts.length} active {alerts.length === 1 ? 'alert' : 'alerts'}
          </p>
        </div>
        {(user.role === 'administrator' || user.role === 'project_manager') && alerts.length > 0 && (
          <Button onClick={sendReminders} disabled={sending}>
            <Send className="h-4 w-4 mr-2" />
            {sending ? 'Sending...' : 'Send Reminders'}
          </Button>
        )}
      </div>

      {/* Alert Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Alerts</p>
                <p className="text-2xl font-bold">{alerts.length}</p>
              </div>
              <Bell className="h-8 w-8 text-gray-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-2 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600">Critical</p>
                <p className="text-2xl font-bold text-red-600">{criticalAlerts.length}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-2 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-700">High Priority</p>
                <p className="text-2xl font-bold text-orange-700">{highAlerts.length}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-2 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-700">Medium</p>
                <p className="text-2xl font-bold text-yellow-700">{mediumAlerts.length}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-yellow-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts List */}
      {alerts.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">All Clear!</h3>
            <p className="text-gray-600">No alerts at this time. Your project is on track.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {alerts.map((alert, index) => (
            <Card key={index} className={`border-l-4 ${getSeverityColor(alert.severity)}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className="mt-1">
                      {getSeverityIcon(alert.severity)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <Badge variant="outline" className="capitalize">
                          {alert.entity_type}
                        </Badge>
                        <Badge className={getSeverityColor(alert.severity)}>
                          {alert.severity}
                        </Badge>
                      </div>
                      <p className="font-medium text-gray-900">{alert.message}</p>
                      {alert.entity_name && (
                        <p className="text-sm text-gray-600 mt-1">
                          {alert.entity_name}
                        </p>
                      )}
                      {alert.days_overdue !== undefined && (
                        <p className="text-sm text-red-600 mt-1">
                          {alert.days_overdue} days overdue
                        </p>
                      )}
                      {alert.days_remaining !== undefined && (
                        <p className="text-sm text-orange-700 mt-1">
                          Due in {alert.days_remaining} days
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="ml-4">
                    {getAlertIcon(alert.type)}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default SmartAlerts;
